import { StanButton, StanInput, StanTextArea } from '@/components';
import { useStoreItem } from '@/features/theme-editor/hooks/useStoreItem';
import { TestimonialsDataSchema } from '@/features/theme-editor/schema/store';
import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { TestimonialsItem } from '@/features/theme-editor/types/store';
import z from 'zod';
import { Panel, PanelFooter } from '../../Panel';
import { Icon } from '../../ui/Icon';
import { ImageUpload } from '../../ui/ImageUpload';
import { Label } from '../../ui/Label';

interface TestimonialItemType {
    image: string;
    quote: string;
    source: string;
}

interface Props {
    id: number;
}

const TestimonialsDataEditSchema = TestimonialsDataSchema.extend({
    testimonials: z
        .object({
            testimonials: z
                .array(
                    z.object({
                        image: z.string(),
                        quote: z.string(),
                        source: z.string(),
                    })
                )
                .optional(),
        })
        .optional(),
});
const Item = ({
    ...props
}: TestimonialItemType & { onUpdate?: (item: TestimonialItemType) => void; onDelete?: () => void }) => {
    const { image, quote, source, onUpdate, onDelete } = props;

    const handleUpdate = (item: TestimonialItemType) => {
        onUpdate?.(item);
    };

    const maxQuoteCount = 160;
    const maxSourceCount = 40;

    return (
        <div className='border-neutral-subtle flex flex-col rounded-lg border text-sm'>
            <ImageUpload
                className='rounded-b-0 aspect-2/1'
                aspectRatio={1 / 1}
                image={image}
                onUpdate={src => handleUpdate?.({ image: src, quote, source })}
                onAdd={src => handleUpdate?.({ image: src, quote, source })}
            />
            <div className='flex flex-col items-center gap-3 p-3 py-4'>
                <div className='w-full space-y-2'>
                    <Label
                        label='Quote'
                        required={
                            !TestimonialsDataSchema.shape.testimonials.shape.testimonials.element.shape.quote.isOptional()
                        }
                        currentCount={quote?.length?.toString() ?? '0'}
                        totalCount={maxQuoteCount.toString()}
                    >
                        <StanTextArea
                            id='quote'
                            name='quote'
                            placeholder='“The Inner Rhythm course changed how I move through my week. I no longer feel guilty for needing rest.”'
                            onChange={event =>
                                handleUpdate({ image, quote: event.target.value.slice(0, maxQuoteCount) ?? '', source })
                            }
                            value={quote}
                            rows={4}
                        />
                    </Label>

                    <Label
                        label='Source'
                        required={
                            !TestimonialsDataSchema.shape.testimonials.shape.testimonials.element.shape.source.isOptional()
                        }
                        currentCount={source?.length?.toString() ?? '0'}
                        totalCount={maxSourceCount.toString()}
                    >
                        <StanInput
                            id='source'
                            name='source'
                            placeholder='— Ravi T., Startup Founder'
                            onChange={event =>
                                handleUpdate({
                                    image,
                                    quote,
                                    source: event.target.value.slice(0, maxSourceCount) ?? '',
                                })
                            }
                            value={source}
                        />
                    </Label>
                </div>

                <StanButton
                    name='remove'
                    variant='link'
                    color='destructive'
                    className='mx-auto w-fit'
                    onClick={() => onDelete?.()}
                >
                    Remove
                </StanButton>
            </div>
        </div>
    );
};

export const Testimonials = ({ id }: Props) => {
    const item = useStoreItem(id);
    const { updateItem } = useCustomizationStore();

    const validation = TestimonialsDataEditSchema.safeParse(item?.data ?? { testimonials: { testimonials: [] } });

    if (!item || !validation.success) {
        console.error('Testimonials data validation failed:', validation.error);
        return null;
    }

    const data = validation.data;
    const { title } = data;
    const items = data.testimonials?.testimonials ?? [];

    const handleUpdate = (index: number, testimonialItem: TestimonialItemType) => {
        updateItem({
            ...item,
            data: {
                ...data,
                testimonials: {
                    ...data.testimonials,
                    testimonials: items.map((iItem, i) => (i === index ? testimonialItem : iItem)),
                },
            },
        } as TestimonialsItem);
    };

    const handleDelete = (index: number) => {
        updateItem({
            ...item,
            data: {
                ...data,
                testimonials: { ...data.testimonials, testimonials: items.filter((_, i) => i !== index) },
            },
        } as TestimonialsItem);
    };

    const handleAdd = () => {
        updateItem({
            ...item,
            data: {
                ...data,
                testimonials: { ...data.testimonials, testimonials: [...items, { image: '', quote: '', source: '' }] },
            },
        } as TestimonialsItem);
    };

    const maxCount = 120;

    return (
        <Panel footer={<PanelFooter id={id} />}>
            <Label
                label='Section title'
                required={!TestimonialsDataSchema.shape.title.isOptional()}
                currentCount={title?.length?.toString() ?? '0'}
                totalCount={maxCount.toString()}
            >
                <StanInput
                    name='title'
                    placeholder='Reviews & Testimonials'
                    onChange={event => {
                        updateItem({
                            ...item,
                            data: { ...data, title: event.target.value.slice(0, maxCount) ?? '' },
                        } as TestimonialsItem);
                    }}
                    value={title ?? ''}
                />
            </Label>

            <div className='mx-auto flex w-8/10 flex-col gap-3'>
                {items.map((item, index) => (
                    <Item
                        key={item.image ?? `testimonial-${index}`}
                        {...item}
                        onUpdate={item => handleUpdate(index, item)}
                        onDelete={() => handleDelete(index)}
                    />
                ))}
            </div>

            <div className='flex justify-center' key={id}>
                <StanButton name='add' onClick={handleAdd} icon={<Icon name='add' />} color='secondary' size='sm'>
                    Add another
                </StanButton>
            </div>
        </Panel>
    );
};
